import { graphql, navigate } from 'gatsby';
import React, { useState } from 'react';

import ContentfulImage from '../components/Common/ContentfulImage';
import DropDown from '../components/Common/DropDown';
import { ArrowLink } from '../components/Common/Links';
import RichTextConverter from '../components/Common/RichText/RichTextConverter';
import Layout from '../components/Layouts/Layout';
import MarketingSeo from '../components/MarketingSeo';
import PageSection from '../components/PageSections/PageSection';
import * as styles from './industry.module.scss';

const IndustryPage = ({ data: { page }, location, pageContext }) => {
  const [displayAmt, setDisplayAmt] = useState(11);

  const path = location.pathname;
  let footerData;
  if (pageContext.industrySlug) {
    footerData = page.industryFooters.find((footer) => footer.industry?.slug === pageContext.industrySlug);
  }

  const sortPref = [
    'Ecommerce & Retail',
    'Travel & Hospitality',
    'Finance & Insurance',
    'SaaS',
    'Gaming',
    'Food & Beverage',
  ];

  const customSort = (a, b) => {
    const aIndex = sortPref.indexOf(a.title);
    const bIndex = sortPref.indexOf(b.title);

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    if (aIndex !== -1) {
      return -1;
    }
    if (bIndex !== -1) {
      return 1;
    }

    return 0;
  };

  let industryLinks = pageContext.industries.sort(customSort);

  let ddOptions = industryLinks?.map((industry) => {
    return {
      label: industry.title,
      value: industry.slug,
    };
  });

  ddOptions.unshift({ label: 'All Industries', value: 'all' });

  const setIndustry = (slug) => {
    navigate(`/customer-story/${slug}/`);
  };

  const loadMore = () => setDisplayAmt(displayAmt + 12);
  const currentDDPlaceholder = pageContext.industrySlug
    ? ddOptions.find((opt) => opt.value === pageContext.industrySlug)?.label
    : 'All Industries';
  return (
    <Layout>
      <PageSection
        gapFix={true}
        isTopSection={true}
        className={[styles.evergreen, styles.header].join(' ')}
        data-analytics-section={'industry-page-header'}
      >
        <div className={styles.headerContainer}>
          <h1>{page.heading}</h1>
        </div>
      </PageSection>
      <PageSection
        slim={true}
        fullBleed={true}
        className={[styles.evergreen, styles.reducePad].join(' ')}
        data-analytics-component={'industry-selector'}
      >
        <div className={styles.dropDownContainer}>
          <DropDown
            outlined={true}
            aria-label={currentDDPlaceholder}
            placeholder={currentDDPlaceholder}
            options={ddOptions}
            noOptionsMessage={() => 'Select Industry'}
            onChange={(choice) => setIndustry(choice?.value)}
          />
        </div>
        <ul className={styles.listContainer}>
          <li data-analytics-component={'industry-list-item'} data-analytics-id={'all'} className={styles.industryItem}>
            <a
              className={[path === `/customer-story/all/` ? styles.active : ''].join(' ')}
              href={'/customer-story/all/'}
            >
              All
            </a>
          </li>
          {industryLinks
            ?.map((industry, i) => {
              return (
                <li
                  data-analytics-component={'industry-list-item'}
                  data-analytics-id={industry.slug}
                  className={styles.industryItem}
                  key={i}
                >
                  <a
                    className={[path === `/customer-story/${industry.slug}/` ? styles.active : ''].join(' ')}
                    href={`/customer-story/${industry.slug}/`}
                  >
                    {industry.title}
                  </a>
                </li>
              );
            })
            .sort(customSort)}
        </ul>
      </PageSection>
      <PageSection className={styles.noPad} data-analytics-section={'industry-results'}>
        <div>
          <ul className={styles.cards}>
            {pageContext.caseStudies?.map((card, i) => {
              if (i > displayAmt) return null;
              return (
                <li
                  data-analytics-component={'industry-card'}
                  data-analytics-id={card.slug}
                  className={[styles.card, styles[`bg${i % 9}`]].join(' ')}
                  key={i}
                >
                  <div>
                    <span className={styles.cardSuper}>Customer Story</span>
                    <div className={styles.cardHeader}>{card.heading}</div>
                    <ArrowLink className={styles.arrow} label={'Read the full story'} url={`/${card.slug}`} />{' '}
                  </div>
                  <div>
                    <img className={styles.cardImage} alt={card.title} src={card.pageMetaData?.ogImage?.file?.url} />{' '}
                  </div>
                </li>
              );
            })}
          </ul>
          {displayAmt < pageContext.caseStudies?.length - 1 && (
            <div className={styles.loadMore}>
              <button onClick={loadMore}>Load More</button>
            </div>
          )}
        </div>
      </PageSection>

      {footerData && (
        <PageSection slim={true} className={styles.footer}>
          <div className={styles.ctaGrid}>
            <div>
              <h3>{footerData.title}</h3>
              <p>{footerData.description?.description}</p>
              <div>
                <ArrowLink className={styles.ctaArrow} label={footerData.ctaLabel} url={footerData.url} />
              </div>
            </div>
            <div>
              <ul>
                {footerData.relatedContent?.map((item, i) => {
                  return (
                    <li className={styles.ctaListItem} key={i}>
                      <div>
                        <ContentfulImage imageFragment={item.image} />
                      </div>
                      <div className={styles.ctaListItemCopy}>
                        <h4>{item.title}</h4>
                        <RichTextConverter copy={item.body} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </PageSection>
      )}
    </Layout>
  );
};

export default IndustryPage;

export const Head = ({ data: { page }, location, pageContext }) => {
  let meta = page.pageMetaData;
  if (pageContext.industrySlug) {
    const found = page.industryMetaData?.find((m) => m.industryType?.slug === pageContext.industrySlug);
    meta = found || meta;
  }
  return <MarketingSeo meta={meta} slug={location.pathname} />;
};

export const query = graphql`
  query IndustryQuery($slug: String!) {
    page: contentfulPageIndustry(slug: { eq: $slug }) {
      ... on Node {
        ...IndustryPage
      }
    }
  }
`;
